export type LabColorDict = {
  L: string
  a: string
  b: string
}

type LabColorObject = {
  L?: number | string | null
  a?: number | string | null
  b?: number | string | null
}

export type LabColorInput =
  | LabColorObject
  | Array<number | string | null | undefined>

const formatComponent = (
  value: number | string | null | undefined,
): string | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n.toFixed(2)
}

const readLabComponents = (
  color: Record<string, unknown>,
): [unknown, unknown, unknown] => [
  color.L ?? color.l,
  color.a ?? color.A,
  color.b ?? color.B,
]

const unwrapLabSource = (color: unknown): unknown => {
  if (color == null || typeof color !== 'object' || Array.isArray(color)) {
    return color
  }
  const obj = color as Record<string, unknown>
  if (
    obj.targetLAB_before_firing &&
    typeof obj.targetLAB_before_firing === 'object'
  ) {
    return obj.targetLAB_before_firing
  }
  if (obj.targetLAB && typeof obj.targetLAB === 'object') {
    return obj.targetLAB
  }
  return color
}

export const toLabColor = (
  color: LabColorInput | null | undefined,
): LabColorDict | null => {
  if (color == null) return null

  const source = unwrapLabSource(color)

  const components = Array.isArray(source)
    ? [source[0], source[1], source[2]]
    : source && typeof source === 'object'
      ? readLabComponents(source as Record<string, unknown>)
      : [undefined, undefined, undefined]

  const L = formatComponent(components[0] as number | string | null | undefined)
  const a = formatComponent(components[1] as number | string | null | undefined)
  const b = formatComponent(components[2] as number | string | null | undefined)

  if (L === null || a === null || b === null) return null

  return { L, a, b }
}

export const formatLab = (value: unknown): string | null => {
  const lab = toLabColor(value as LabColorInput)
  if (!lab) return null
  return `L*${lab.L}, a*${lab.a}, b*${lab.b}`
}

export const formatProvidedInputValue = (value: unknown): string => {
  const lab = formatLab(value)
  if (lab) return lab
  if (Array.isArray(value)) {
    return value.filter((item) => item != null && item !== '').join(', ')
  }
  if (value == null) return ''
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([key]) => !key.startsWith('__'))
      .map(([, nested]) => formatProvidedInputValue(nested))
      .filter(Boolean)
      .join(', ')
  }
  return String(value)
}
